import Select from 'components/Select';
import { CaptionsSpokenLanguage } from 'features/application/types';
import {
  fontSizeChanged,
  selectCaptionsApplyToAll,
  selectCaptionsFontSize,
  selectCaptionsSpokenLanguage,
  spokenLanguageApplyToAllChanged,
  spokenLanguageChanged,
} from 'features/transcripts/slices/captions/captionsSlice';
import React from 'react';
import { TabPanelInner } from 'components/Tabs';
import { FormControl, MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import InputLabel from 'components/InputLabel';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import SettingControl from 'components/SettingControl';
import { Switch } from 'components/Switch';
import { PermissionTypes } from 'features/permissions/types';
import { usePermissions } from 'features/permissions/usePermissions';
import { getCaptionsLanguageLabel } from 'features/transcripts/utils/getCaptionsLanguageLabel';

const languages: { code: CaptionsSpokenLanguage; nested?: boolean }[] = [
  // { code: 'zh' },
  // { code: 'zh-CN', nested: true },
  // { code: 'zh-TW', nested: true },
  { code: 'bg' },
  { code: 'ca' },
  { code: 'da' },
  { code: 'en' },
  // { code: 'en-AU', nested: true },
  // { code: 'en-GB', nested: true },
  // { code: 'en-IN', nested: true },
  // { code: 'en-NZ', nested: true },
  // { code: 'en-US', nested: true },
  { code: 'fi' },
  { code: 'fr' },
  // { code: 'fr-CA', nested: true },
  { code: 'de' },
  { code: 'ro' },
  // { code: 'ar' },
  // { code: 'hi' } ,
  // { code: 'hi-Latn', nested: true },
  // { code: 'id' },
  { code: 'it' },
  { code: 'nl' },
  // { code: 'ja' },
  // { code: 'ko' },
  // { code: 'no' },
  // { code: 'pl' },
  { code: 'pt' },
  // { code: 'pt-BR', nested: true },
  // { code: 'ru' },
  { code: 'es' },
  // { code: 'es-419', nested: true },
  { code: 'sv' },
  // { code: 'ta' },
  { code: 'tr' },
  // { code: 'uk' },
];

const fontSizes = [16, 24, 32];

const CaptionsTab = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');

  const language = useAppSelector(selectCaptionsSpokenLanguage);
  const applyCaptionsToAll = useAppSelector(selectCaptionsApplyToAll);
  const fontSize = useAppSelector(selectCaptionsFontSize);

  const { hasPermissions } = usePermissions();
  const canTranscribeSession = hasPermissions(PermissionTypes.transcribeSession);

  const handleLanguageChange = (e: SelectChangeEvent<CaptionsSpokenLanguage>) => {
    dispatch(spokenLanguageChanged({ code: e.target.value }));
  };

  const handleFontSizeChange = (e: SelectChangeEvent<number>) => {
    dispatch(fontSizeChanged(e.target.value));
  };

  const handleApplyToAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(spokenLanguageApplyToAllChanged(e.target.checked));

    if (e.target.checked) {
      dispatch(spokenLanguageChanged({ code: language, applyToAll: true }));
    }
  };

  const renderLanguageSelectValue = (value: string) => getCaptionsLanguageLabel(value);

  return (
    <TabPanelInner>
      <Stack spacing={5}>
        <FormControl fullWidth>
          <label htmlFor="spoken-language" id="spoken-language-label">
            <Typography>{t('captions.spoken_language_label')}</Typography>
            <Typography
              component="span"
              sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem' }}
            >
              {t('captions.spoken_language_help')}
            </Typography>
          </label>
          <Select
            sx={{ mt: '8px !important' }}
            labelId="spoken-language-label"
            id="spoken-language"
            onChange={handleLanguageChange}
            value={language}
            renderValue={renderLanguageSelectValue}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    maxHeight: 500,
                  },
                },
              },
            }}
          >
            {languages.map(({ code, nested }, i) => (
              <MenuItem key={i} value={code} sx={nested ? { pl: 4 } : { fontWeight: 400 }}>
                {t(`captions.languages.${code}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {canTranscribeSession && (
          <div>
            <SettingControl
              label={t('captions.apply_spoken_language_to_all.label')}
              helperText={t('captions.apply_spoken_language_to_all.help')}
              alignItems="flex-start"
              control={
                <Switch
                  onChange={handleApplyToAllChange}
                  checked={applyCaptionsToAll}
                  slotProps={{
                    input: {
                      'aria-label': applyCaptionsToAll
                        ? t('captions.apply_spoken_language_to_all.switch_disable_aria_label')
                        : t('captions.apply_spoken_language_to_all.switch_enable_aria_label'),
                    },
                  }}
                />
              }
            />
          </div>
        )}

        <FormControl fullWidth>
          <InputLabel id="font-size-label">{t('captions.font_size_label')}</InputLabel>
          <Select
            labelId="font-size-label"
            id="font_size"
            onChange={handleFontSizeChange}
            value={fontSize}
          >
            {fontSizes.map((code, i) => (
              <MenuItem key={i} value={code}>
                {t(`captions.fontSizes.${code}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </TabPanelInner>
  );
};
export default CaptionsTab;
